import { AuthConfig, Hub, ResourcesConfig } from '@aws-amplify/core';
import { cognitoUserPoolsTokenProvider, refreshAuthTokens } from '@aws-amplify/auth/cognito';
import { environment } from '@env/environment';

/**
 * Amplify's token refresher signature. `@aws-amplify/auth/cognito` exports the default refresher
 * but not the type, so it is derived here.
 */
export type TokenRefreshInput = Parameters<typeof refreshAuthTokens>[0];
export type TokenRefresher = (input: TokenRefreshInput) => ReturnType<typeof refreshAuthTokens>;

/**
 * Makes Amplify refresh tokens against the Cognito pool that issued them.
 *
 * Why this exists: the `aws-amplify` `Amplify.configure()` wrapper only pushes the pool config
 * into `cognitoUserPoolsTokenProvider` on its *first* call. Later calls, such as the SSO callback
 * reconfiguring for `environment.cognitoSsoConfig`, update `Amplify.getConfig()` but leave the
 * token store and the refresher on the standard pool. Two things follow:
 *
 * 1. SSO tokens are stored under the *standard* pool's cookie key prefix. That is what lets a fresh
 *    page load of the main app, and every Angular Element, read them without knowing the user signed
 *    in through SSO. This is relied upon and deliberately left alone.
 * 2. The default refresher sends the SSO pool's refresh token to the *standard* pool. Cognito rejects
 *    it with NotAuthorizedException, Amplify treats that as a definitive auth failure and clears the
 *    shared session cookies, and the user is logged out at the first token expiry.
 *
 * This module fixes point 2 by choosing the pool from the stored access token at refresh time,
 * matching on its `client_id` claim first and the `iss` pool id second.
 */

interface CognitoPoolIdentity {
  userPoolId?: string;
  userPoolClientId?: string;
}

function poolIdentity(config: ResourcesConfig | undefined): CognitoPoolIdentity | undefined {
  return config?.Auth?.Cognito as unknown as CognitoPoolIdentity | undefined;
}

/**
 * Resolves which of the candidate pool configurations issued the given tokens.
 *
 * @returns the matching `Auth` config, or `undefined` when the tokens carry no usable claims or
 * no candidate matches (callers fall back to whatever Amplify would have used).
 */
export function resolveAuthConfigForTokens(
  tokens: TokenRefreshInput['tokens'] | undefined,
  candidates: readonly ResourcesConfig[]
): AuthConfig | undefined {
  const payload = tokens?.accessToken?.payload;
  if (!payload) {
    return undefined;
  }

  const clientId = typeof payload['client_id'] === 'string' ? payload['client_id'] : undefined;
  const issuerPoolId = typeof payload.iss === 'string' ? payload.iss.split('/').pop() : undefined;

  const findBy = (predicate: (pool: CognitoPoolIdentity) => boolean): AuthConfig | undefined =>
    candidates.find((candidate) => {
      const pool = poolIdentity(candidate);
      return !!pool && predicate(pool);
    })?.Auth;

  if (clientId) {
    const byClient = findBy((pool) => pool.userPoolClientId === clientId);
    if (byClient) {
      return byClient;
    }
  }

  if (issuerPoolId) {
    return findBy((pool) => pool.userPoolId === issuerPoolId);
  }

  return undefined;
}

/**
 * Wraps a refresher so the pool/client used for the refresh call is the one that issued the tokens.
 * Falls back to the config Amplify passed in when the issuer cannot be identified.
 */
export function createPoolAwareTokenRefresher(
  candidates: readonly ResourcesConfig[],
  refresh: TokenRefresher = refreshAuthTokens
): TokenRefresher {
  return (input) =>
    refresh({
      ...input,
      authConfig: resolveAuthConfigForTokens(input.tokens, candidates) ?? input.authConfig
    });
}

export interface PoolAwareTokenRefresherOptions {
  /** Pool configurations that may have issued the stored tokens. Defaults to the standard and SSO pools. */
  candidates?: readonly ResourcesConfig[];
  /** Invoked with the error whenever Amplify reports a `tokenRefresh_failure`. The latest handler wins. */
  onRefreshFailure?: (error: unknown) => void;
  /** Underlying refresh call. Defaults to Amplify's; exposed for tests. */
  refresh?: TokenRefresher;
}

let latestFailureHandler: ((error: unknown) => void) | undefined;
let failureListenerRegistered = false;

/**
 * Installs the pool-aware refresher on Amplify's shared token orchestrator. Safe to call repeatedly;
 * call it after `Amplify.configure(...)` and `setKeyValueStorage(...)` in every Amplify consumer
 * (the main app bootstrap and each Angular Element).
 */
export function installPoolAwareTokenRefresher(options: PoolAwareTokenRefresherOptions = {}): void {
  const candidates = options.candidates ?? defaultPoolCandidates();
  cognitoUserPoolsTokenProvider.tokenOrchestrator.setTokenRefresher(createPoolAwareTokenRefresher(candidates, options.refresh));

  latestFailureHandler = options.onRefreshFailure ?? latestFailureHandler;
  if (!failureListenerRegistered) {
    failureListenerRegistered = true;
    Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'tokenRefresh_failure') {
        latestFailureHandler?.(payload.data?.error);
      }
    });
  }
}

function defaultPoolCandidates(): ResourcesConfig[] {
  return [environment.cognitoConfig, environment.cognitoSsoConfig].filter((config): config is ResourcesConfig => !!config);
}

/**
 * Human-readable summary of a refresh failure, suitable as an Error message for Rollbar.
 */
export function describeTokenRefreshFailure(error: unknown): string {
  const named = error as { name?: unknown; message?: unknown } | undefined;
  const name = typeof named?.name === 'string' ? named.name : 'UnknownError';
  const message = typeof named?.message === 'string' ? named.message : String(error);
  return `Cognito token refresh failed: ${name}: ${message}`;
}

/**
 * True when Cognito rejected the refresh because the refresh token has outlived its lifetime. Cognito
 * refresh tokens expire a fixed period after sign-in (an app client setting) and are not extended by
 * use, so this is the normal end of a session rather than a fault -- the only way on is to sign in again.
 */
export function isRefreshTokenExpired(error: unknown): boolean {
  const named = error as { name?: unknown; message?: unknown } | undefined;
  return named?.name === 'NotAuthorizedException' && typeof named.message === 'string' && /refresh token has expired/i.test(named.message);
}
